/**
 * ChallengeLogPanel — the human challenge log for a run.
 *
 * Records where a human overrode, disputed or explicitly confirmed a
 * model-generated claim. Aggregated onto the Board Decision Pack so a reviewer
 * sees the outputs were scrutinised, not accepted blindly. Part of the
 * defensibility chain (confidence bands → coverage → crosswalk → challenge log).
 */
import React, { useCallback, useEffect, useState } from "react";
import { MessageSquareWarning, Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const CLAIM_CLASSES = ["model_inference", "client_fact", "evidence_finding", "recommendation", "assumption", "hypothesis"];
const ACTIONS = [
  { v: "override", label: "Overrode", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  { v: "dispute", label: "Disputed", cls: "text-red-700 bg-red-50 border-red-200" },
  { v: "confirm", label: "Confirmed", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
];
const ACTION_META = Object.fromEntries(ACTIONS.map((a) => [a.v, a]));

export default function ChallengeLogPanel({ runId, locked }) {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ claim: "", claim_class: "model_inference", action: "override", from_value: "", to_value: "", rationale: "" });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/challenges`);
      setLog(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load challenge log");
    } finally { setLoading(false); }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.claim.trim()) { toast.error("Describe the claim being challenged"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/challenges`, form);
      setLog(data);
      setForm({ claim: "", claim_class: "model_inference", action: "override", from_value: "", to_value: "", rationale: "" });
      setOpen(false);
      toast.success("Challenge logged");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log challenge");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/assessments/runs/${runId}/challenges/${id}`);
      setLog(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="challenge-log-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
          <MessageSquareWarning size={14} /> Human challenge log
        </div>
        <button type="button" onClick={load} disabled={loading}
          className="text-zinc-400 hover:text-[#7737F6] disabled:opacity-50" title="Refresh" data-testid="challenge-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <p className="text-[12px] text-zinc-500 mb-3" data-testid="challenge-summary">
        {log?.summary || "Record where a human overrode, disputed or confirmed a model output — it prints on the Board Decision Pack."}
      </p>

      {log?.entries?.length > 0 && (
        <ul className="space-y-2 mb-3" data-testid="challenge-list">
          {log.entries.map((e) => {
            const m = ACTION_META[e.action] || ACTION_META.override;
            return (
              <li key={e.id} className="border border-zinc-200 p-2.5 text-[12px]" data-testid={`challenge-item-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>
                  {e.id !== "assurance_rejection" && !locked && (
                    <button type="button" onClick={() => remove(e.id)} disabled={busy}
                      className="text-zinc-300 hover:text-red-600 disabled:opacity-50" title="Remove" data-testid={`challenge-delete-${e.id}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
                <div className="font-medium text-zinc-800 mt-1">{e.claim}</div>
                <div className="text-[10px] text-zinc-400 capitalize">{(e.claim_class || "").replace(/_/g, " ")}</div>
                {(e.from_value || e.to_value) && (
                  <div className="text-zinc-600 mt-1">{e.from_value || "—"} → {e.to_value || "—"}</div>
                )}
                {e.rationale && <div className="text-zinc-500 italic mt-1">{e.rationale}</div>}
              </li>
            );
          })}
        </ul>
      )}

      {!locked && (
        open ? (
          <div className="border-t border-zinc-100 pt-3 space-y-2" data-testid="challenge-form">
            <input value={form.claim} onChange={set("claim")} placeholder="Claim being challenged (e.g. AI-drafted incident narrative)"
              className="w-full border border-zinc-300 px-3 py-2 text-[13px]" data-testid="challenge-claim-input" />
            <div className="flex gap-2">
              <select value={form.action} onChange={set("action")} className="border border-zinc-300 px-2 py-2 text-[13px] flex-1" data-testid="challenge-action-select">
                {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
              </select>
              <select value={form.claim_class} onChange={set("claim_class")} className="border border-zinc-300 px-2 py-2 text-[13px] flex-1" data-testid="challenge-class-select">
                {CLAIM_CLASSES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <input value={form.from_value} onChange={set("from_value")} placeholder="From (optional)" className="w-full border border-zinc-300 px-3 py-2 text-[13px]" data-testid="challenge-from-input" />
              <input value={form.to_value} onChange={set("to_value")} placeholder="To (optional)" className="w-full border border-zinc-300 px-3 py-2 text-[13px]" data-testid="challenge-to-input" />
            </div>
            <textarea value={form.rationale} onChange={set("rationale")} rows={2} placeholder="Rationale — why the human judgement differs…"
              className="w-full border border-zinc-300 px-3 py-2 text-[13px]" data-testid="challenge-rationale-input" />
            <div className="flex gap-2">
              <button type="button" onClick={add} disabled={busy} className="ek-btn ek-btn-primary text-[13px] disabled:opacity-50" data-testid="challenge-save-btn">Log challenge</button>
              <button type="button" onClick={() => setOpen(false)} className="text-[12px] text-zinc-500 hover:text-black" data-testid="challenge-cancel-btn">Cancel</button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setOpen(true)}
            className="ek-btn ek-btn-outline text-[13px] inline-flex items-center gap-1.5 w-full justify-center" data-testid="challenge-add-btn">
            <Plus size={14} /> Log a human challenge
          </button>
        )
      )}
    </div>
  );
}
